import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { loadAcordLibrary, resolveFileUrl, downloadFileBlob } from '../../api/acordApi';
import './AcordLibrary.css';

// A record's real edition is what the document's own footer says, not what
// EZLynx listed. They disagree on 10 records; the footer wins.
const verifiedEdition = r => r.footerEdition || r.edition;

const NON_CANONICAL_REASON = {
  section_excerpt: 'Section excerpt — not the full form',
  pdfill_resave: 'Third-party re-save — prefer the original',
  duplicate_content: 'Duplicate of another record',
};

const basename = p => (p || '').split('/').pop();

// Editions sort chronologically; three malformed source values sort last
// rather than corrupting the order.
const editionSortKey = ed => (/^\d{4}-(0[1-9]|1[0-2])$/.test(ed || '') ? ed : '0000-00');

// `strictForms` holds numeric tokens that exactly name a real form number.
// Without this, searching "20 2016" also matches forms 21-25, because "20" is a
// substring of the edition "2016-03" - 100 results instead of the one form.
function tokenMatches(rec, token, strictForms) {
  const ed = verifiedEdition(rec);
  const text = rec._haystack;
  if (/^\d{1,3}$/.test(token)) {
    if (strictForms.has(token)) return String(rec.formNum) === token;
    return ed.includes(token) || text.includes(token);
  }
  if (/^\d{4}(-\d{1,2})?$/.test(token)) {
    return ed.startsWith(token) || (rec.edition || '').startsWith(token);
  }
  if (/^[a-z]{2}$/.test(token)) {
    return (rec.primaryState || '').toLowerCase() === token
      || (rec.states || []).some(s => s.toLowerCase() === token)
      || text.includes(token);
  }
  return text.includes(token);
}

function gapMatches(gap, token, strictForms) {
  const text = `${gap.formNum} ${gap.edition} ${gap.baseTitle || ''} ${gap.state || ''}`.toLowerCase();
  if (/^\d{1,3}$/.test(token)) {
    if (strictForms.has(token)) return String(gap.formNum) === token;
    return text.includes(token);
  }
  if (/^\d{4}(-\d{1,2})?$/.test(token)) return (gap.edition || '').startsWith(token);
  if (/^[a-z]{2}$/.test(token)) return (gap.state || '').toLowerCase() === token || text.includes(token);
  return text.includes(token);
}

export default function AcordLibrary({ getToken }) {
  const [records, setRecords] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [lobFilter, setLobFilter] = useState('');
  const [canonicalOnly, setCanonicalOnly] = useState(true);

  const [copiedId, setCopiedId] = useState(null);
  const [rowNote, setRowNote] = useState({});   // id -> "Still syncing" etc.
  const [openingId, setOpeningId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const { records: recs, gaps: gp } = await loadAcordLibrary(token);
        if (cancelled) return;
        recs.forEach(r => {
          r._haystack = [
            r.baseTitleNormalized, r.namedFile, r.formName, r.primaryState,
            r.edition, r.footerEdition, (r.states || []).join(' '),
          ].filter(Boolean).join(' ').toLowerCase();
        });
        setRecords(recs);
        setGaps(gp || []);
        setLoadState('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e.message);
        setLoadState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // --- CURRENT badge: newest verified edition per form + state -----------
  const currentIds = useMemo(() => {
    const newest = new Map();
    records.forEach(r => {
      if (!r.canonical) return;
      const key = `${r.formNum}|${r.primaryState || ''}`;
      const k = editionSortKey(verifiedEdition(r));
      const prev = newest.get(key);
      if (!prev || k > prev.k) newest.set(key, { k, id: r.id });
    });
    return new Set([...newest.values()].map(v => v.id));
  }, [records]);

  // --- field-count divergence: same form + edition + state, >10% apart ---
  // Keyed this tightly on purpose: form+edition alone flags 46 groups, nearly
  // all of which are legitimate per-state variants.
  const divergence = useMemo(() => {
    const groups = new Map();
    records.forEach(r => {
      if (r.fieldCount == null) return;
      const key = `${r.formNum}|${r.edition}|${r.primaryState || ''}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });
    const out = new Map();
    groups.forEach(group => {
      if (group.length < 2) return;
      const counts = group.map(r => r.fieldCount);
      const max = Math.max(...counts), min = Math.min(...counts);
      if (!max || (max - min) / max <= 0.10) return;
      group.forEach(r => {
        if (r.fieldCount < max) out.set(r.id, { mine: r.fieldCount, max });
      });
    });
    return out;
  }, [records]);

  const states = useMemo(() => {
    const set = new Set(records.map(r => r.primaryState).filter(Boolean));
    const rest = [...set].filter(s => s !== 'TX' && s !== 'All').sort();
    return ['TX', 'All', ...rest].filter(s => set.has(s));
  }, [records]);

  const lobs = useMemo(
    () => [...new Set(records.flatMap(r => r.lobs || []))].sort(),
    [records]
  );

  const tokens = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query]
  );

  const formNumbers = useMemo(
    () => new Set(records.map(r => String(r.formNum))),
    [records]
  );

  const { groupsList, total } = useMemo(() => {
    const strictForms = new Set(
      tokens.filter(t => /^\d{1,3}$/.test(t) && formNumbers.has(t))
    );
    let hits = records;
    if (canonicalOnly) hits = hits.filter(r => r.canonical);
    if (stateFilter) hits = hits.filter(r => r.primaryState === stateFilter);
    if (lobFilter) hits = hits.filter(r => (r.lobs || []).includes(lobFilter));
    if (tokens.length) hits = hits.filter(r => tokens.every(t => tokenMatches(r, t, strictForms)));

    const gapHits = tokens.length
      ? gaps.filter(g => tokens.every(t => gapMatches(g, t, strictForms)))
      : [];

    const byForm = new Map();
    hits.forEach(r => {
      if (!byForm.has(r.formNum)) byForm.set(r.formNum, { formNum: r.formNum, rows: [], gaps: [] });
      byForm.get(r.formNum).rows.push(r);
    });
    gapHits.forEach(g => {
      if (!byForm.has(g.formNum)) byForm.set(g.formNum, { formNum: g.formNum, rows: [], gaps: [] });
      byForm.get(g.formNum).gaps.push(g);
    });

    const exact = tokens.find(t => /^\d{1,3}$/.test(t));
    const list = [...byForm.values()];
    list.forEach(g => g.rows.sort((a, b) =>
      editionSortKey(verifiedEdition(b)).localeCompare(editionSortKey(verifiedEdition(a)))
      || (a.primaryState || '').localeCompare(b.primaryState || '')));
    list.sort((a, b) => {
      if (exact) {
        const ae = String(a.formNum) === exact, be = String(b.formNum) === exact;
        if (ae !== be) return ae ? -1 : 1;
      }
      return a.formNum - b.formNum;
    });
    return { groupsList: list, total: hits.length };
  }, [records, gaps, tokens, formNumbers, canonicalOnly, stateFilter, lobFilter]);

  const copyFilename = useCallback(async (rec, e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(basename(rec.namedFile));
      setCopiedId(rec.id);
      setTimeout(() => setCopiedId(c => (c === rec.id ? null : c)), 1500);
    } catch {
      setRowNote(n => ({ ...n, [rec.id]: 'Could not copy' }));
    }
  }, []);

  // Pull the bytes and hand them to the browser, so the form lands on disk and
  // opens in the user's default PDF handler rather than Chrome's viewer.
  const downloadRecord = useCallback(async (rec, e) => {
    e.stopPropagation();
    setDownloadingId(rec.id);
    setRowNote(n => ({ ...n, [rec.id]: null }));
    let objectUrl = null;
    try {
      const token = await getToken();
      const blob = await downloadFileBlob(token, rec.vaultPath);
      if (!blob) {
        setRowNote(n => ({ ...n, [rec.id]: 'Still syncing' }));
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = basename(rec.namedFile);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setRowNote(n => ({ ...n, [rec.id]: 'Still syncing' }));
    } finally {
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      setDownloadingId(null);
    }
  }, [getToken]);

  const openRecord = useCallback(async (rec) => {
    setOpeningId(rec.id);
    setRowNote(n => ({ ...n, [rec.id]: null }));
    try {
      const token = await getToken();
      const url = await resolveFileUrl(token, rec.vaultPath);
      if (!url) {
        setRowNote(n => ({ ...n, [rec.id]: 'Still syncing' }));
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch {
      setRowNote(n => ({ ...n, [rec.id]: 'Still syncing' }));
    } finally {
      setOpeningId(null);
    }
  }, [getToken]);

  if (loadState === 'loading') {
    return <div className="acord"><div className="acord-status">Loading ACORD library…</div></div>;
  }
  if (loadState === 'error') {
    return (
      <div className="acord">
        <div className="acord-status acord-status--error">
          Could not load the ACORD library.<div className="acord-status-detail">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="acord">
      <header className="acord-header">
        <h2 className="acord-title">ACORD Library</h2>
        <div className="acord-count">{records.length.toLocaleString()} forms</div>
      </header>

      <input
        ref={searchRef}
        className="acord-search"
        type="search"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Form number, edition, title, state — e.g. 35 2011, or cancellation"
        autoComplete="off"
      />

      <button className="acord-filter-toggle" onClick={() => setShowFilters(s => !s)}>
        {showFilters ? '−' : '+'} Filters
        {(stateFilter || lobFilter || !canonicalOnly) && <span className="acord-filter-dot" />}
      </button>

      {showFilters && (
        <div className="acord-filters">
          <label>
            State
            <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
              <option value="">All states</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>
            Line of business
            <select value={lobFilter} onChange={e => setLobFilter(e.target.value)}>
              <option value="">Any LOB</option>
              {lobs.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="acord-check">
            <input
              type="checkbox"
              checked={canonicalOnly}
              onChange={e => setCanonicalOnly(e.target.checked)}
            />
            Canonical only
          </label>
        </div>
      )}

      <div className="acord-resultcount">
        {total.toLocaleString()} {total === 1 ? 'form' : 'forms'}
      </div>

      <div className="acord-results">
        {groupsList.length === 0 && (
          <div className="acord-empty">No forms match that search.</div>
        )}

        {groupsList.map(group => {
          const multi = group.rows.length + group.gaps.length > 1;
          return (
            <section key={group.formNum} className="acord-group">
              {multi && (
                <div className="acord-group-head">
                  ACORD {group.formNum}
                  <span className="acord-group-count">
                    {group.rows.length} {group.rows.length === 1 ? 'edition' : 'editions'}
                  </span>
                </div>
              )}

              {group.rows.map(rec => {
                const ed = verifiedEdition(rec);
                const div = divergence.get(rec.id);
                const note = rowNote[rec.id];
                return (
                  <div
                    key={rec.id}
                    className="acord-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => openRecord(rec)}
                    onKeyDown={e => { if (e.key === 'Enter') openRecord(rec); }}
                  >
                    <div className="acord-row-ident">
                      <span className="acord-form">ACORD {rec.formNum}</span>
                      <span className="acord-edition">{ed}</span>
                      {currentIds.has(rec.id) && <span className="acord-badge acord-badge--current">CURRENT</span>}
                    </div>

                    <div className="acord-row-body">
                      <div className="acord-row-title">
                        {rec.primaryState && <span className="acord-state">{rec.primaryState}</span>}
                        {rec.baseTitleNormalized}
                      </div>

                      <div className="acord-row-meta">
                        {rec.pageCount != null && <span>{rec.pageCount} pp</span>}
                        {rec.fieldCount != null && <span>{rec.fieldCount} fields</span>}
                        {openingId === rec.id && <span className="acord-opening">Opening…</span>}
                        {note && <span className="acord-syncing">{note}</span>}
                      </div>

                      {!rec.canonical && rec.nonCanonicalReason && (
                        <div className="acord-badge acord-badge--warn">
                          {NON_CANONICAL_REASON[rec.nonCanonicalReason] || rec.nonCanonicalReason}
                        </div>
                      )}
                      {rec.footerStatus === 'MISMATCH' && (
                        <div className="acord-badge acord-badge--warn">
                          Document is actually ACORD {rec.footerForm} ({rec.footerEdition})
                        </div>
                      )}
                      {div && (
                        <div className="acord-badge acord-badge--warn">
                          Fewer fields than another copy of this edition ({div.mine} vs {div.max})
                        </div>
                      )}
                    </div>

                    <div className="acord-actions">
                      <button
                        className="acord-copy"
                        onClick={e => copyFilename(rec, e)}
                        title={basename(rec.namedFile)}
                      >
                        {copiedId === rec.id ? 'Copied' : 'Copy name'}
                      </button>
                      <button
                        className="acord-copy acord-download"
                        onClick={e => downloadRecord(rec, e)}
                        disabled={downloadingId === rec.id}
                        title={`Download ${basename(rec.namedFile)}`}
                      >
                        {downloadingId === rec.id ? 'Downloading…' : 'Download'}
                      </button>
                    </div>
                  </div>
                );
              })}

              {group.gaps.map(gap => (
                <div key={`gap-${gap.formNum}-${gap.edition}`} className="acord-row acord-row--gap">
                  <div className="acord-row-ident">
                    <span className="acord-form">ACORD {gap.formNum}</span>
                    <span className="acord-edition">{gap.edition}</span>
                  </div>
                  <div className="acord-row-body">
                    <div className="acord-row-title">
                      {gap.state && <span className="acord-state">{gap.state}</span>}
                      {gap.baseTitle}
                    </div>
                    <div className="acord-gap-note">
                      Not held — EZLynx lists this edition but serves a different document.
                      {gap.whatIsServedInstead && <> Serves {gap.whatIsServedInstead}.</>}
                    </div>
                  </div>
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
